import React, { useRef, useEffect, useState } from "react";
import { Drawer } from "antd";
import Menu from "./_components/Menu/Menu";
import Header from "./_components/Header/Header";
import Personal from "./_components/Personal/Personal";
import Score from "./_components/Score/Score";
import Mortgages from "./_components/Mortgages/Mortgages";
import Installments from "./_components/Installments/Installments";
import Revolving from "./_components/Revolving/Revolving";
import Inquiries from "./_components/Inquiries/Inquiries";
import { flatMoney } from "_assets/js/helpers";
import "./Transunion.scss";

// TransUnion-specific mappings
const accountRatingMap = {
  "01": "Current",
  "02": "30 days late",
  "03": "60 days late",
  "04": "90 days late",
  "05": "120 days late",
  "07": "Making payments under wage earner plan",
  "08": "Repossession",
  "09": "Bad debt; placed for collection",
  "9B": "Collection account",
};

function Transunion(props) {
  const [scrollTops, setScrollTops] = useState(undefined);
  const [selected, setSelected] = useState("personal");
  const [view, setView] = useState("applicant");
  const scrollRef = useRef(null);

  const report =
    view === "applicant" ? props?.report : props?.coApplicantReport;

  // Combine trades and collections for TransUnion
  const allTrades = [
    ...(report?.custom?.credit?.trades || []),
    ...(report?.custom?.credit?.collections || []),
  ];

  const mortgages = allTrades.filter((t) => {
    return t?.portfolioType === "MORTGAGE";
  });

  const installment = allTrades.filter((t) => {
    return t?.portfolioType === "INSTALLMENT";
  });

  const revolving = allTrades.filter((t) => {
    return t?.portfolioType === "REVOLVING";
  });

  // Open accounts (collections, etc.)
  const openAccounts = allTrades.filter((t) => {
    return t?.portfolioType === "OPEN";
  });

  const scrollToChild = (childId) => {
    const childOffsetTop = document.querySelector(
      `#trade-${childId}`
    )?.offsetTop;
    if (!childOffsetTop) return;
    scrollRef?.current?.scrollTo({
      top: childOffsetTop - 20,
      behavior: "smooth",
    });
  };

  const FORGIVENESS = 400;
  const handleScroll = (tops) => {
    if (scrollRef.current) {
      const curr = scrollRef.current.scrollTop;
      Object.keys(tops || []).forEach((key) => {
        const el = tops[key];
        if (
          curr > el.top - FORGIVENESS &&
          curr < el.top + el.height - FORGIVENESS
        ) {
          setSelected(key);
        }
      });
    }
  };

  useEffect(() => {
    if (
      scrollRef.current &&
      scrollRef.current.getAttribute("listener") !== "true" &&
      !scrollTops
    ) {
      const childElements = document.querySelectorAll("[id^='trade-']");
      const tops = {};
      childElements.forEach((el) => {
        const id = el.id.replace("trade-", "");
        tops[id] = {
          top: el?.offsetTop,
          height: el?.offsetHeight,
        };
      });
      setScrollTops(tops);
      scrollRef.current.addEventListener("scroll", () => handleScroll(tops));
    }
    // eslint-disable-next-line
  }, [props.open]);

  const reportProps = {
    report,
    mortgages,
    installment,
    revolving,
    scrollToChild,
    selected,
    inquiries: report?.custom?.credit?.inquiries || [],
    openAccounts,
  };

  return (
    <Drawer
      width={800}
      onClose={props.onClose}
      open={props.open}
      className="transunion-drawer"
    >
      <Header {...props} report={report} close={props.onClose} view={view} />
      <Menu
        {...props}
        {...reportProps}
        view={view}
        setView={setView}
        hasCoApplicant={!!props?.coApplicantReport}
      />
      <div className="report" ref={scrollRef}>
        <Personal id="trade-personal" {...reportProps} />
        <Score id="trade-score" {...reportProps} />
        <div id="trade-mortgages">
          <Mortgages {...reportProps} />
        </div>
        <div id="trade-installment">
          <Installments {...reportProps} />
        </div>
        <div id="trade-revolving">
          <Revolving {...reportProps} />
        </div>
        {openAccounts && openAccounts.length > 0 && (
          <div id="trade-collections">
            <div className="wzrd-box collections">
              <div className="wzrd-header">
                <h3>Collections</h3>
              </div>
              <div className="wzrd-body">
                {openAccounts.map((account, i) => (
                  <div
                    key={`collection-${i}`}
                    className="collection-item"
                    style={{
                      marginBottom: "16px",
                      padding: "12px",
                      border: "1px solid #ececec",
                      borderRadius: "8px",
                    }}
                  >
                    <h4>{account?.subscriber?.name?.unparsed}</h4>
                    <div
                      style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}
                    >
                      <div>
                        <strong>Account:</strong> {account?.accountNumber}
                      </div>
                      <div>
                        <strong>Balance:</strong>{" "}
                        {flatMoney(parseInt(account?.currentBalance || "0"))}
                      </div>
                      <div>
                        <strong>Original Creditor:</strong>{" "}
                        {account?.original?.creditGrantor?.unparsed || "-"}
                      </div>
                      <div>
                        <strong>Status:</strong>{" "}
                        {accountRatingMap[account?.accountRating] ||
                          account?.accountRating}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        <Inquiries id="trade-inquiries" {...reportProps} />
      </div>
    </Drawer>
  );
}

export default Transunion;
